import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useAuth } from '../contexts/AuthContext'

const registerSchema = z
  .object({
    displayName: z.string().min(3, 'Mínimo 3 caracteres'),
    email: z.string().email('Email inválido'),
    password: z.string().min(6, 'Mínimo 6 caracteres'),
    confirmPassword: z.string(),
    referralCode: z.string().optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Las contraseñas no coinciden',
    path: ['confirmPassword'],
  })

type RegisterFormData = z.infer<typeof registerSchema>

interface RegisterModalProps {
  isOpen: boolean
  onClose: () => void
  onSwitchToLogin: () => void
}

export function RegisterModal({
  isOpen,
  onClose,
  onSwitchToLogin,
}: RegisterModalProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { register: registerUser } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<'demo' | 'pending' | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  })

  const referralCode = watch('referralCode')

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true)
    setError(null)

    try {
      const result = await registerUser(
        data.displayName,
        data.email,
        data.password,
        data.referralCode?.trim() || undefined
      )
      if (result.success) {
        if (result.isDemo) {
          setSuccess('demo')
          // Demo users go directly to lobby
          setTimeout(() => {
            handleClose()
            navigate('/lobby')
          }, 2000)
        } else {
          setSuccess('pending')
        }
        reset()
      } else {
        setError(result.error || 'Error al crear la cuenta. Intenta de nuevo.')
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Error al crear la cuenta. Intenta de nuevo.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    reset()
    setError(null)
    setSuccess(null)
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="bg-casino-black-light border-casino-gold/30 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-cinzel text-2xl text-casino-gold text-center">
            {t('auth.createAccount')}
          </DialogTitle>
        </DialogHeader>

        {success === 'demo' ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-6"
          >
            <div className="text-6xl mb-4">🎰</div>
            <h3 className="text-xl text-casino-gold mb-2">{t('auth.welcomeDemo')}</h3>
            <p className="text-gray-400 mb-4">
              {t('auth.demoAccountCreated')}
            </p>
            <p className="text-sm text-gray-500">
              {t('auth.redirectingToLobby')}
            </p>
          </motion.div>
        ) : success === 'pending' ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-6"
          >
            <div className="text-6xl mb-4">✉️</div>
            <h3 className="text-xl text-casino-gold mb-2">{t('auth.accountCreated')}</h3>
            <p className="text-gray-400 mb-6">
              {t('auth.pendingApproval')}
            </p>
            <Button variant="gold" onClick={onSwitchToLogin}>
              {t('auth.goToLogin')}
            </Button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4">
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-red-900/30 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg text-sm"
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            <div className="space-y-2">
              <Label htmlFor="displayName" className="text-casino-gold/80">
                {t('auth.username')}
              </Label>
              <Input
                id="displayName"
                type="text"
                placeholder={t('auth.yourName')}
                {...register('displayName')}
                className="bg-casino-black border-casino-gold/30 focus:border-casino-gold"
              />
              {errors.displayName && (
                <p className="text-red-400 text-sm">{errors.displayName.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-casino-gold/80">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="tu@email.com"
                {...register('email')}
                className="bg-casino-black border-casino-gold/30 focus:border-casino-gold"
              />
              {errors.email && (
                <p className="text-red-400 text-sm">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-casino-gold/80">
                {t('auth.password')}
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                {...register('password')}
                className="bg-casino-black border-casino-gold/30 focus:border-casino-gold"
              />
              {errors.password && (
                <p className="text-red-400 text-sm">{errors.password.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-casino-gold/80">
                {t('auth.confirmPassword')}
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                {...register('confirmPassword')}
                className="bg-casino-black border-casino-gold/30 focus:border-casino-gold"
              />
              {errors.confirmPassword && (
                <p className="text-red-400 text-sm">{errors.confirmPassword.message}</p>
              )}
            </div>

            {/* Referral Code - Optional */}
            <div className="space-y-2">
              <Label htmlFor="referralCode" className="text-casino-gold/80">
                {t('auth.referralCode')} <span className="text-gray-500">({t('auth.optional')})</span>
              </Label>
              <Input
                id="referralCode"
                type="text"
                placeholder={t('auth.enterCodeIfYouHave')}
                {...register('referralCode')}
                className="bg-casino-black border-casino-gold/30 focus:border-casino-gold"
              />
            </div>

            {/* Info box based on referral code */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={`p-3 rounded-lg text-sm ${
                referralCode?.trim()
                  ? 'bg-casino-gold/10 border border-casino-gold/30 text-casino-gold'
                  : 'bg-zinc-800/50 border border-zinc-700 text-gray-400'
              }`}
            >
              {referralCode?.trim() ? (
                <>
                  <span className="font-semibold">🎫 {t('auth.withInvitation')}</span>
                  <p className="mt-1 text-xs opacity-80">{t('auth.willNeedApproval')}</p>
                </>
              ) : (
                <>
                  <span className="font-semibold">🎮 {t('auth.demoMode')}</span>
                  <p className="mt-1 text-xs opacity-80">{t('auth.playFreeNoDeposit')}</p>
                </>
              )}
            </motion.div>

            <Button
              type="submit"
              variant="gold"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading
                ? t('auth.creatingAccount')
                : referralCode?.trim()
                  ? t('auth.requestMembership')
                  : t('auth.createDemoAccount')
              }
            </Button>

            <div className="text-center text-sm text-gray-400">
              {t('auth.alreadyHaveAccount')}{' '}
              <button
                type="button"
                onClick={onSwitchToLogin}
                className="text-casino-gold hover:text-casino-gold-light transition-colors"
              >
                {t('auth.login')}
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
